import React from 'react';
import type { ThemeToggleProps } from './ThemeToggle.types';
import './ThemeToggle.css';

/**
 * ThemeToggle Component
 *
 * A button component for toggling between light and dark themes.
 * Displays a moon icon in light mode and a sun icon in dark mode.
 *
 * @example
 * ```tsx
 * const [theme, setTheme] = useState<'light' | 'dark'>('light');
 *
 * <ThemeToggle theme={theme} onThemeChange={setTheme} />
 * ```
 */
export const ThemeToggle: React.FC<ThemeToggleProps> = ({
  theme,
  onThemeChange,
  className = '',
  ariaLabel = 'Toggle theme',
  icons
}) => {
  const handleToggle = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    onThemeChange(newTheme);
  };

  const defaultLightIcon = (
    // Moon icon - shown in light mode to indicate "switch to dark"
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  );

  const defaultDarkIcon = (
    // Sun icon - shown in dark mode to indicate "switch to light"
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="5" />
      <line x1="12" y1="1" x2="12" y2="3" />
      <line x1="12" y1="21" x2="12" y2="23" />
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
      <line x1="1" y1="12" x2="3" y2="12" />
      <line x1="21" y1="12" x2="23" y2="12" />
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
    </svg>
  );

  const lightIcon = icons?.light || defaultLightIcon;
  const darkIcon = icons?.dark || defaultDarkIcon;

  return (
    <button
      className={`theme-toggle ${className}`}
      onClick={handleToggle}
      aria-label={ariaLabel}
      aria-pressed={theme === 'dark'}
      type="button"
    >
      {theme === 'light' ? lightIcon : darkIcon}
    </button>
  );
};
